/**
 * NutritionOnboarding - Complete onboarding flow for nutrition tracking
 * Step 1: Fitness Goal (Abnehmen, Muskelaufbau, Fit bleiben)
 * Step 2: Body Data (Height, Weight - from profile or manual)
 * Step 3: Sports Activities (detailed sports with duration & frequency)
 * Step 4: Daily Routine (Sleep, Work, Leisure distribution)
 * Step 5: Diet Type (Alles, Vegetarisch, Vegan, etc.)
 * Step 6: Calorie Goal Calculation
 * Step 7: Allergies & Intolerances
 * Step 8: AI Validation (KI prüft die Werte)
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Loader2, Sparkles, CheckCircle, Zap, CalendarDays } from 'lucide-react';
import axios from 'axios';

// Import step components
import {
  StepFitnessGoal,
  StepBodyData,
  StepCalorieGoal,
  StepDietType,
  StepAllergies,
  StepFoodPreferences,
  StepLifestyle,
  CYCLE_TYPES,
  ACTIVITY_LEVELS,
  DAILY_ACTIVITY_LEVELS,
} from './onboarding';
import StepAIValidation from './onboarding/StepAIValidation';
import StepDailyRoutine from './onboarding/StepDailyRoutine';
import StepSportsActivities, { SPORT_DATABASE } from './onboarding/StepSportsActivities';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Legacy fitness goals (for backwards compatibility - not extracted)
const FITNESS_GOALS = [
  { 
    id: 'weight_loss', 
    label: 'Abnehmen', 
    description: 'Körperfett reduzieren',
    color: 'blue',
    tips: 'Kaloriendefizit, mehr Protein, regelmässig bewegen'
  },
  { 
    id: 'muscle_building', 
    label: 'Muskelaufbau', 
    description: 'Muskelmasse aufbauen',
    color: 'green',
    tips: 'Kalorienüberschuss, viel Protein, Krafttraining'
  },
  { 
    id: 'maintenance', 
    label: 'Fit bleiben', 
    description: 'Gewicht & Fitness halten',
    color: 'yellow',
    tips: 'Ausgewogene Ernährung, regelmässig bewegen'
  }
];

const NutritionOnboarding = ({ 
  open, 
  onClose, 
  token, 
  onComplete,
  customerId = null, // For admin mode - edit specific customer
  isAdmin = false,   // Admin mode flag
  existingData = null // Pre-fill with existing data (for admin)
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Check if user has skipped/dismissed this session
  const hasSkippedThisSession = typeof window !== 'undefined' && sessionStorage.getItem('nutrition_onboarding_skipped');
  
  // Form data
  const [prefs, setPrefs] = useState({
    fitness_goal: null,
    cycle_type: null, // NEW: Zyklus-Typ
    height_cm: null,
    weight_kg: null,
    age: null, // NEW
    gender: null, // NEW
    activity_level: 'moderate', // For Variant A (Weekly) - fallback only
    activity_variant: 'weekly', // NEW: 'weekly' or 'daily'
    daily_activity_level: 'durchschnittlich', // For Variant B (Daily)
    work_activity_level: 'buero', // NEW: Work/Job activity level
    // BMR Source - NEW: allows manual BMR from diagnostic measurements
    bmr_source: 'calculated', // 'calculated' or 'manual'
    manual_bmr: null, // Manual BMR value from diagnostic (InBody etc.)
    bmr_measurement_device: '', // Device name
    // Sports Activities - NEW: detailed sports with duration & frequency
    sports_activities: [], // Array of { id, name, duration, frequency, met, intensity }
    diet_program: 'keine',
    avoid_foods: [],
    prefer_foods: [],
    intolerances: [],
    // Iter 299: Lifestyle & Geschmack Defaults
    meal_pattern: '3_meals',
    cooking_time: 'medium',
    budget_level: 'normal',
    taste_profile: [],
    religious_diet: null,
    seasonal_preference: true,
    onboarding_completed: false
  });
  
  const [bodyDataFromProfile, setBodyDataFromProfile] = useState({
    height_cm: null,
    weight_kg: null,
    age: null,
    gender: null
  });
  
  const [newAvoidFood, setNewAvoidFood] = useState('');
  const [newPreferFood, setNewPreferFood] = useState('');
  
  // NEW: Calculated goals
  const [calculatedGoals, setCalculatedGoals] = useState(null);
  const [calcSettings, setCalcSettings] = useState(null);
  const [bodyComposition, setBodyComposition] = useState(null);
  const [aiValidationResult, setAiValidationResult] = useState(null);
  const [healthKitData, setHealthKitData] = useState(null); // Apple Watch/HealthKit data

  // Steps: 1=Variant, 2=Goal, 3=Body, 4=Activity(weekly), 5=DailyRoutine(weekly), 6=Diet, 7=Calories, 8=Allergies, 9=AI
  const isDaily = prefs.activity_variant === 'daily';
  const totalSteps = 11;
  const effectiveSteps = isDaily ? 9 : 11; // Skip 2 steps for daily variant
  const currentEffectiveStep = isDaily && step > 3 ? step - 2 : step;
  const progress = (currentEffectiveStep / effectiveSteps) * 100;

  // Skip onboarding for this session
  const handleSkip = () => {
    sessionStorage.setItem('nutrition_onboarding_skipped', 'true');
    toast.info('Du kannst das Onboarding später in den Einstellungen nachholen.');
    onClose();
  };

  useEffect(() => {
    if (open && !dataLoaded) {
      setStep(1);
      setAiValidationResult(null);
      setCalculatedGoals(null);
      if (existingData) {
        loadExistingPreferences();
        loadCalcSettings();
        loadBodyComposition();
        loadHealthKitData();
      } else {
        loadOnboardingData();
      }
    }
    if (!open) {
      setDataLoaded(false);
    }
  }, [open]);

  // Apply onboarding data to state - use explicit null checks to preserve falsy-but-valid values
  const applyOnboardingData = (responseData) => {
    const { preferences: data, calc_settings, body_composition, healthkit } = responseData;
    if (data) {
      // Build the new prefs object explicitly to avoid || operator swallowing valid values
      const newPrefs = {
        fitness_goal: data.fitness_goal ?? null,
        cycle_type: data.cycle_type ?? data.fitness_goal ?? null,
        height_cm: data.height_cm ?? data.body_data_from_profile?.height_cm ?? null,
        weight_kg: data.weight_kg ?? data.body_data_from_profile?.weight_kg ?? null,
        age: data.age ?? data.body_data_from_profile?.age ?? null,
        gender: data.gender ?? data.body_data_from_profile?.gender ?? null,
        activity_level: data.activity_level ?? 'moderate',
        activity_variant: data.activity_variant ?? 'weekly',
        daily_activity_level: data.daily_activity_level ?? 'durchschnittlich',
        work_activity_level: data.work_activity_level ?? 'buero',
        bmr_source: data.bmr_source && data.bmr_source !== '' ? data.bmr_source : 'calculated',
        manual_bmr: data.manual_bmr != null ? data.manual_bmr : null,
        bmr_measurement_device: data.bmr_measurement_device ?? '',
        bmr_measurement_date: data.bmr_measurement_date ?? null,
        sports_activities: data.sports_activities ?? [],
        diet_program: data.diet_program ?? 'keine',
        avoid_foods: data.avoid_foods ?? [],
        prefer_foods: data.prefer_foods ?? [],
        intolerances: data.intolerances ?? [],
        daily_routine: data.daily_routine ?? null,
        protein_per_kg: data.protein_per_kg ?? null,
        fat_per_kg: data.fat_per_kg ?? null,
        onboarding_completed: data.onboarding_completed ?? false
      };
      setPrefs(newPrefs);
      setBodyDataFromProfile({
        height_cm: data.body_data_from_profile?.height_cm ?? null,
        weight_kg: data.body_data_from_profile?.weight_kg ?? null,
        age: data.body_data_from_profile?.age ?? null,
        gender: data.body_data_from_profile?.gender ?? null
      });
    }
    if (calc_settings) setCalcSettings(calc_settings);
    if (body_composition) setBodyComposition(body_composition);
    if (healthkit?.available && healthkit?.data) setHealthKitData(healthkit.data);
  };

  // Combined data loading with cache
  const loadOnboardingData = async () => {
    try {
      // Try cache first for instant display
      const cached = sessionStorage.getItem('onboarding_data_cache');
      if (cached) {
        applyOnboardingData(JSON.parse(cached));
        setDataLoaded(true);
      }
      
      // Always fetch fresh data (updates cache + state)
      const response = await axios.get(`${API}/portal/nutrition/onboarding-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      sessionStorage.setItem('onboarding_data_cache', JSON.stringify(response.data));
      applyOnboardingData(response.data);
    } catch (error) {
      console.error('Load onboarding data error:', error);
    } finally {
      setDataLoaded(true);
    }
  };


  const loadCalcSettings = async () => {
    try {
      const response = await axios.get(`${API.replace('/api', '')}/api/settings/nutrition-calculation/public`);
      setCalcSettings(response.data);
    } catch (error) {
      console.log('Using default calculation settings');
    }
  };

  const loadBodyComposition = async () => {
    try {
      // Use different endpoint for admin vs customer
      const endpoint = isAdmin && customerId
        ? `${API}/nutrition/customer/${customerId}/body-composition-adjustment`
        : `${API}/portal/nutrition/body-composition-adjustment`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBodyComposition(response.data);
    } catch (error) {
      console.log('Body composition data not available');
    }
  };

  // Load Apple HealthKit / health tracking data
  const loadHealthKitData = async () => {
    try {
      const response = await axios.get(`${API}/portal/nutrition/healthkit/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.available && response.data.data) {
        setHealthKitData(response.data.data);
        console.log('HealthKit data loaded:', response.data.data);
      }
    } catch (error) {
      console.log('HealthKit data not available');
    }
  };

  // Calculate BMR using the configured formula
  const calculateBMR = (weight, height, age, gender) => {
    const formula = calcSettings?.bmr_formula || 'mifflin_st_jeor';
    
    if (formula === 'harris_benedict') {
      if (gender === 'male') {
        return 88.36 + (13.4 * weight) + (4.8 * height) - (5.7 * age);
      } else {
        return 447.6 + (9.2 * weight) + (3.1 * height) - (4.3 * age);
      }
    } else {
      // Mifflin-St Jeor (default)
      if (gender === 'male') {
        return (10 * weight) + (6.25 * height) - (5 * age) + 5;
      } else {
        return (10 * weight) + (6.25 * height) - (5 * age) - 161;
      }
    }
  };

  // Calculate goals based on body data and cycle type
  const calculateGoals = () => {
    const { height_cm, weight_kg, age, gender, cycle_type, activity_level, activity_variant, daily_activity_level, work_activity_level, bmr_source, manual_bmr, sports_activities } = prefs;
    
    // For manual BMR, we only need cycle_type and activity_level
    if (bmr_source === 'manual' && manual_bmr) {
      if (!cycle_type) return null;
    } else {
      // For calculated BMR, we need all body params
      if (!height_cm || !weight_kg || !age || !gender || !cycle_type) {
        return null;
      }
    }

    const activityMultipliers = calcSettings?.activity_multipliers || {
      sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9
    };
    
    // Work activity bonus (kcal/day)
    const workBonuses = calcSettings?.work_activity_levels || {
      buero: { kcal_bonus: 0 },
      leicht_stehend: { kcal_bonus: 200 },
      stehend_gehend: { kcal_bonus: 350 },
      koerperlich_leicht: { kcal_bonus: 500 },
      koerperlich_mittel: { kcal_bonus: 650 },
      koerperlich_schwer: { kcal_bonus: 800 }
    };

    // Calculate or use manual BMR
    let bmr;
    let bmrSource = bmr_source || 'calculated';
    
    if (bmr_source === 'manual' && manual_bmr) {
      bmr = manual_bmr;
      bmrSource = 'manual';
    } else {
      bmr = calculateBMR(parseFloat(weight_kg), parseFloat(height_cm), parseInt(age), gender);
      bmrSource = 'calculated';
    }
    
    // Calculate sports calories (NEW: from detailed sports activities)
    let sportsCaloriesWeekly = 0;
    let sportsCaloriesDaily = 0;
    let sportsSummary = [];
    const userWeight = parseFloat(weight_kg) || 70;
    
    if (sports_activities && sports_activities.length > 0) {
      const INTENSITY_MULTIPLIERS = { leicht: 0.75, mittel: 1.0, intensiv: 1.35 };
      sports_activities.forEach(sport => {
        const baseMet = sport.met || 5.0;
        const intensityMult = INTENSITY_MULTIPLIERS[sport.intensity || 'mittel'] || 1.0;
        const met = baseMet * intensityMult;
        const caloriesPerSession = met * userWeight * (sport.duration / 60);
        const weeklyCalories = caloriesPerSession * sport.frequency;
        sportsCaloriesWeekly += weeklyCalories;
        sportsSummary.push({
          name: sport.name,
          frequency: sport.frequency,
          duration: sport.duration,
          intensity: sport.intensity || 'mittel',
          caloriesPerSession: Math.round(caloriesPerSession),
          weeklyCalories: Math.round(weeklyCalories)
        });
      });
      sportsCaloriesDaily = Math.round(sportsCaloriesWeekly / 7);
    }
    
    // Calculate TDEE based on activity variant
    let tdee;
    let workBonus = 0;
    let tdeeWithWork;
    let activityDescription;
    
    // Get sedentary baseline factor from admin settings (default 1.2)
    const sedFactor = calcSettings?.sedentary_baseline_factor || calcSettings?.activity_multipliers?.sedentary || 1.2;
    
    if (activity_variant === 'daily') {
      // Variante B (Täglich): nur BMR als statische Basis. Aktivität, Sport
      // & Training werden LIVE vom Apple Watch / HealthKit on-top addiert.
      // KEIN Aktivitäts-Multiplikator, KEIN Work-Bonus, KEIN Daily-Adjustment
      // → sonst Doppelzählung mit Wearable-Daten (User-Report 06.05.2026).
      tdee = bmr;
      tdeeWithWork = bmr;
      activityDescription = 'Nur Grundumsatz · Aktivität live von Wearable';
    } else {
      // Variant A: Calculate TDEE from base BMR + sports + work activity
      tdee = bmr * sedFactor; // Base metabolism without exercise
      
      // Add sports calories from detailed activities (if available)
      if (sportsCaloriesDaily > 0) {
        tdee += sportsCaloriesDaily;
        activityDescription = `Basis + ${sports_activities.length} Sportart${sports_activities.length > 1 ? 'en' : ''} (+${sportsCaloriesDaily} kcal/Tag)`;
        
        // Add work activity bonus ONLY with sport-based calculation
        // (sedentary base 1.2 doesn't include work activity)
        if (prefs.daily_routine && prefs.daily_routine.work_hours_per_week > 0) {
          const routine = prefs.daily_routine;
          const workHoursDaily = (routine.work_hours_per_week || 40) / 5;
          const workType = routine.work_type || 'sitzend';
          
          // MET values per work type
          const workMETs = { sitzend: 1.5, stehend: 2.0, koerperlich: 3.5 };
          const avgMET = workMETs[workType] || 1.5;
          const weight = parseFloat(prefs.weight_kg) || 70;
          const workCalories = avgMET * weight * 1.05 * workHoursDaily;
          const baselineWorkCalories = 1.3 * weight * 1.05 * workHoursDaily;
          workBonus = Math.round(workCalories - baselineWorkCalories);
          
          // Add leisure activity bonus (light activity hours above sedentary baseline)
          const leisureLightHours = routine.leisure_light_activity_hours || 0;
          if (leisureLightHours > 0) {
            const leisureLightCalories = 2.5 * weight * 1.05 * leisureLightHours;
            const leisureBaselineCalories = 1.3 * weight * 1.05 * leisureLightHours;
            workBonus += Math.round(leisureLightCalories - leisureBaselineCalories);
          }
          
          const workLabel = { sitzend: 'Büro', stehend: 'Stehend/Gehend', koerperlich: 'Körperlich' }[workType] || 'Büro';
          activityDescription += ` + ${workLabel} ${routine.work_hours_per_week}h/Wo${leisureLightHours > 0 ? ' & Freizeit' : ''} (+${workBonus} kcal)`;
        } else {
          workBonus = workBonuses[work_activity_level]?.kcal_bonus || 0;
          if (workBonus > 0) {
            activityDescription += ` + Arbeit (+${workBonus} kcal)`;
          }
        }
      } else {
        // Fallback to activity level multipliers (includes daily activity + work)
        // NO separate work bonus here - multiplier already accounts for it
        const multiplier = activityMultipliers[activity_level] || 1.55;
        tdee = bmr * multiplier;
        activityDescription = `${ACTIVITY_LEVELS.find(l => l.id === activity_level)?.label || 'Moderat aktiv'} (×${multiplier})`;
      }
      
      tdeeWithWork = tdee + workBonus;
    }
    
    // Apply body composition adjustment (if data available)
    let bodyCompAdjustment = 0;
    let bodyCompDescription = null;
    if (bodyComposition?.body_composition_enabled && bodyComposition?.has_body_data) {
      bodyCompAdjustment = bodyComposition.kcal_adjustment || 0;
      bodyCompDescription = {
        body_fat_percent: bodyComposition.body_fat_percent,
        category: bodyComposition.category,
        category_info: bodyComposition.category_info,
        adjustment: bodyCompAdjustment,
        data_source: bodyComposition.data_source
      };
    }
    
    // Apply cycle type adjustment
    const selectedCycle = CYCLE_TYPES.find(c => c.id === cycle_type);
    const adjustmentPercent = selectedCycle?.adjustment || 0;
    
    // Calculate final calories: TDEE + work bonus + body comp adjustment + cycle adjustment
    const baseCaloriesWithBodyComp = Math.round(tdeeWithWork + bodyCompAdjustment);
    const dailyCalories = Math.round(baseCaloriesWithBodyComp * (1 + adjustmentPercent / 100));
    
    // Get macros from Admin settings (macro_presets_by_goal) if available, otherwise use cycle type defaults
    const adminMacroPresets = calcSettings?.macro_presets_by_goal || {};
    const adminPreset = adminMacroPresets[cycle_type];
    
    let macros;
    if (adminPreset && adminPreset.protein_percent && adminPreset.carbs_percent && adminPreset.fat_percent) {
      // Use admin-defined macro preset
      macros = { 
        protein: adminPreset.protein_percent, 
        carbs: adminPreset.carbs_percent, 
        fat: adminPreset.fat_percent 
      };
    } else {
      // Fallback to cycle type defaults
      macros = selectedCycle?.macros || { protein: 25, carbs: 50, fat: 25 };
    }
    
    // Calculate protein based on g/kg body weight (priority) or percentage fallback
    const proteinPerKg = prefs.protein_per_kg || selectedCycle?.protein_per_kg || 1.6;
    const weightKg = parseFloat(prefs.weight_kg) || 70;
    const proteinGrams = Math.round(weightKg * proteinPerKg);
    const proteinCalories = proteinGrams * 4;
    const proteinPercent = Math.round((proteinCalories / dailyCalories) * 100);
    
    // Calculate fat based on g/kg body weight or default ratio
    let fatPerKg = prefs.fat_per_kg;
    if (!fatPerKg) {
      // Calculate default fat_per_kg from cycle macros
      const remainingPercentDefault = 100 - proteinPercent;
      const originalCarbsFatRatio = macros.carbs / (macros.carbs + macros.fat);
      const defaultFatPercent = remainingPercentDefault - Math.round(remainingPercentDefault * originalCarbsFatRatio);
      const defaultFatGrams = Math.round((defaultFatPercent / 100) * dailyCalories / 9);
      fatPerKg = Math.round((defaultFatGrams / weightKg) * 10) / 10;
    }
    const fatGrams = Math.round(weightKg * fatPerKg);
    const fatCalories = fatGrams * 9;
    const fatPercent = Math.round((fatCalories / dailyCalories) * 100);
    
    // Carbs = remaining calories after protein + fat
    const carbsCalories = dailyCalories - proteinCalories - fatCalories;
    const carbsGrams = Math.max(0, Math.round(carbsCalories / 4));
    const carbsPercent = Math.max(0, 100 - proteinPercent - fatPercent);

    return {
      bmr: Math.round(bmr),
      bmr_source: bmrSource,
      bmr_measurement_device: prefs.bmr_measurement_device || null,
      tdee: Math.round(tdee),
      work_bonus: workBonus,
      sports_calories_daily: sportsCaloriesDaily,
      sports_calories_weekly: Math.round(sportsCaloriesWeekly),
      sports_summary: sportsSummary,
      tdee_with_work: Math.round(tdeeWithWork),
      tdee_with_body_comp: baseCaloriesWithBodyComp,
      body_comp_adjustment: bodyCompAdjustment,
      body_comp_info: bodyCompDescription,
      daily_calories: dailyCalories,
      adjustment_percent: adjustmentPercent,
      protein_grams: proteinGrams,
      carbs_grams: carbsGrams,
      fat_grams: fatGrams,
      protein_percent: proteinPercent,
      carbs_percent: carbsPercent,
      fat_percent: fatPercent,
      protein_per_kg: proteinPerKg,
      fat_per_kg: fatPerKg,
      activity_variant: activity_variant,
      activity_description: activityDescription
    };
  };

  const loadExistingPreferences = async () => {
    try {
      // If we have existing data (admin mode), use it directly
      if (existingData) {
        setPrefs({
          fitness_goal: existingData.fitness_goal ?? null,
          cycle_type: existingData.cycle_type ?? existingData.fitness_goal ?? null,
          height_cm: existingData.height_cm ?? null,
          weight_kg: existingData.weight_kg ?? null,
          age: existingData.age ?? null,
          gender: existingData.gender ?? null,
          activity_level: existingData.activity_level ?? 'moderate',
          activity_variant: existingData.activity_variant ?? 'weekly',
          daily_activity_level: existingData.daily_activity_level ?? 'durchschnittlich',
          work_activity_level: existingData.work_activity_level ?? 'buero',
          bmr_source: existingData.bmr_source && existingData.bmr_source !== '' ? existingData.bmr_source : 'calculated',
          manual_bmr: existingData.manual_bmr != null ? existingData.manual_bmr : null,
          bmr_measurement_device: existingData.bmr_measurement_device ?? '',
          bmr_measurement_date: existingData.bmr_measurement_date ?? null,
          sports_activities: existingData.sports_activities ?? [],
          diet_program: existingData.diet_program ?? 'keine',
          avoid_foods: existingData.avoid_foods ?? [],
          prefer_foods: existingData.prefer_foods ?? [],
          intolerances: existingData.intolerances ?? [],
          daily_routine: existingData.daily_routine ?? null,
          protein_per_kg: existingData.protein_per_kg ?? null,
          fat_per_kg: existingData.fat_per_kg ?? null,
          onboarding_completed: existingData.onboarding_completed ?? false
        });
        
        setBodyDataFromProfile({
          height_cm: existingData.height_cm ?? null,
          weight_kg: existingData.weight_kg ?? null,
          age: existingData.age ?? null,
          gender: existingData.gender ?? null
        });
        
        setDataLoaded(true);
        return;
      }
      
      // Otherwise load from API (portal mode)
      const response = await axios.get(`${API}/portal/nutrition/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setPrefs({
        fitness_goal: data.fitness_goal ?? null,
        cycle_type: data.cycle_type ?? data.fitness_goal ?? null,
        height_cm: data.height_cm ?? data.body_data_from_profile?.height_cm ?? null,
        weight_kg: data.weight_kg ?? data.body_data_from_profile?.weight_kg ?? null,
        age: data.age ?? data.body_data_from_profile?.age ?? null,
        gender: data.gender ?? data.body_data_from_profile?.gender ?? null,
        activity_level: data.activity_level ?? 'moderate',
        activity_variant: data.activity_variant ?? 'weekly',
        daily_activity_level: data.daily_activity_level ?? 'durchschnittlich',
        work_activity_level: data.work_activity_level ?? 'buero',
        bmr_source: data.bmr_source && data.bmr_source !== '' ? data.bmr_source : 'calculated',
        manual_bmr: data.manual_bmr != null ? data.manual_bmr : null,
        bmr_measurement_device: data.bmr_measurement_device ?? '',
        bmr_measurement_date: data.bmr_measurement_date ?? null,
        sports_activities: data.sports_activities ?? [],
        diet_program: data.diet_program ?? 'keine',
        avoid_foods: data.avoid_foods ?? [],
        prefer_foods: data.prefer_foods ?? [],
        intolerances: data.intolerances ?? [],
        daily_routine: data.daily_routine ?? null,
        protein_per_kg: data.protein_per_kg ?? null,
        fat_per_kg: data.fat_per_kg ?? null,
        onboarding_completed: data.onboarding_completed ?? false
      });
      
      setBodyDataFromProfile({
        height_cm: data.body_data_from_profile?.height_cm ?? null,
        weight_kg: data.body_data_from_profile?.weight_kg ?? null,
        age: data.body_data_from_profile?.age ?? null,
        gender: data.body_data_from_profile?.gender ?? null
      });
    } catch (error) {
      console.error('Load preferences error:', error);
    } finally {
      setDataLoaded(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Determine API endpoints based on admin mode
      const goalsEndpoint = isAdmin && customerId
        ? `${API}/nutrition/customer/${customerId}/goals`
        : `${API}/portal/nutrition/goals`;
      
      const prefsEndpoint = isAdmin && customerId
        ? `${API}/nutrition/customer/${customerId}/preferences`
        : `${API}/portal/nutrition/preferences`;

      // First save the calculated goals
      if (calculatedGoals) {
        await axios.put(goalsEndpoint, {
          daily_calories: calculatedGoals.daily_calories,
          daily_calorie_goal: calculatedGoals.daily_calories,
          daily_protein: calculatedGoals.protein_grams,
          daily_carbs: calculatedGoals.carbs_grams,
          daily_fat: calculatedGoals.fat_grams,
          protein_percent: calculatedGoals.protein_percent,
          carbs_percent: calculatedGoals.carbs_percent,
          fat_percent: calculatedGoals.fat_percent,
          protein_per_kg: calculatedGoals.protein_per_kg || prefs.protein_per_kg,
          fat_per_kg: calculatedGoals.fat_per_kg || prefs.fat_per_kg,
          cycle_type: prefs.cycle_type,
          goal_type: prefs.cycle_type,
          calculated_from_body_data: true,
          activity_variant: prefs.activity_variant,
          // BMR Source
          bmr_source: prefs.bmr_source ?? 'calculated',
          manual_bmr: prefs.manual_bmr ?? null,
          bmr_measurement_device: prefs.bmr_measurement_device ?? null,
          // Daily Routine data
          daily_routine: prefs.daily_routine || null,
          calculation_params: {
            weight_kg: parseFloat(prefs.weight_kg),
            height_cm: parseFloat(prefs.height_cm),
            age: parseInt(prefs.age),
            gender: prefs.gender,
            activity_level: prefs.activity_level,
            activity_variant: prefs.activity_variant,
            daily_activity_level: prefs.daily_activity_level,
            work_activity_level: prefs.work_activity_level,
            bmr: calculatedGoals.bmr,
            bmr_source: calculatedGoals.bmr_source,
            bmr_measurement_device: calculatedGoals.bmr_measurement_device,
            tdee: calculatedGoals.tdee,
            work_bonus: calculatedGoals.work_bonus,
            tdee_with_work: calculatedGoals.tdee_with_work,
            tdee_with_body_comp: calculatedGoals.tdee_with_body_comp,
            body_comp_adjustment: calculatedGoals.body_comp_adjustment,
            body_comp_info: calculatedGoals.body_comp_info,
            adjustment_percent: calculatedGoals.adjustment_percent,
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Then save preferences
      await axios.put(prefsEndpoint, {
        ...prefs,
        onboarding_completed: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Kalorienziel: ${calculatedGoals?.daily_calories || 2000} kcal gespeichert!`);
      // Invalidate cache so next open fetches fresh data
      sessionStorage.removeItem('onboarding_data_cache');
      if (onComplete) onComplete();
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!prefs.activity_variant; // Berechnungsart
      case 2: return !!prefs.cycle_type; // Zyklus-Typ
      case 3: // Körperdaten + BMR
        if (prefs.bmr_source === 'manual') {
          return prefs.manual_bmr > 0;
        }
        return prefs.height_cm > 0 && prefs.weight_kg > 0 && prefs.age > 0 && prefs.gender;
      case 4: // Sportarten (optional)
        return true;
      case 5: // Tagesablauf
        if (!prefs.daily_routine) return true;
        const routine5 = prefs.daily_routine;
        const dailyWork = (routine5.work_hours_per_week || 0) / 5;
        const totalHours5 = (routine5.sleep_hours || 0) + dailyWork + 
          (routine5.leisure_sitting_hours || 0) + (routine5.leisure_light_activity_hours || 0);
        return Math.abs(totalHours5 - 24) < 1.5;
      case 6: return true; // Diet type (optional)
      case 7: return true; // Kalorienziel - always allow (shows message if no goals)
      case 8: return true; // Allergies
      case 9: return true; // Food Preferences (optional)
      case 10: return true; // Lifestyle (optional)
      case 11: return true; // AI validation
      default: return true;
    }
  };

  const handleNext = () => {
    const isDaily = prefs.activity_variant === 'daily';
    let nextStep = step + 1;
    
    // Skip Steps 4 (Sports) and 5 (Daily Routine) for Variante B (Täglich)
    if (isDaily && step === 3) {
      nextStep = 6; // Jump from Body Data directly to Diet Type
    }
    
    // Calculate goals before showing step 7 (Calorie Goal)
    if (nextStep === 7 || step === 6) {
      const goals = calculateGoals();
      setCalculatedGoals(goals);
    }
    
    if (nextStep <= totalSteps) {
      setStep(nextStep);
    } else {
      handleSave();
    }
  };

  // Auto-recalculate goals when prefs change and we're on or past step 7 (Calorie Goal)
  useEffect(() => {
    if (step >= 7) {
      const goals = calculateGoals();
      if (goals) setCalculatedGoals(goals);
    }
  }, [step, prefs.cycle_type, prefs.height_cm, prefs.weight_kg, prefs.age, prefs.gender, prefs.manual_bmr, prefs.bmr_source, prefs.activity_variant, prefs.protein_per_kg, prefs.fat_per_kg, dataLoaded]);

  const handleBack = () => {
    if (step > 1) {
      const isDaily = prefs.activity_variant === 'daily';
      // Skip Steps 5 and 4 going back for Variante B
      if (isDaily && step === 6) {
        setStep(3); // Jump from Diet Type back to Body Data
      } else {
        setStep(step - 1);
      }
    }
  };

  const addAvoidFood = (food) => {
    if (food && !prefs.avoid_foods.includes(food)) {
      setPrefs(prev => ({ ...prev, avoid_foods: [...prev.avoid_foods, food] }));
    }
    setNewAvoidFood('');
  };

  const removeAvoidFood = (food) => {
    setPrefs(prev => ({ ...prev, avoid_foods: prev.avoid_foods.filter(f => f !== food) }));
  };

  const addPreferFood = () => {
    if (newPreferFood && !prefs.prefer_foods.includes(newPreferFood)) {
      setPrefs(prev => ({ ...prev, prefer_foods: [...prev.prefer_foods, newPreferFood] }));
      setNewPreferFood('');
    }
  };

  const toggleIntolerance = (id) => {
    setPrefs(prev => {
      const current = prev.intolerances || [];
      const newIntolerances = current.includes(id)
        ? current.filter(i => i !== id)
        : [...current, id];
      return { ...prev, intolerances: newIntolerances };
    });
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4" data-testid="onboarding-step-variant">
            <div className="text-center mb-6">
              <Zap className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white">Berechnungsart</h3>
              <p className="text-zinc-400 text-sm">Wie soll dein Kalorienziel berechnet werden?</p>
              {!dataLoaded && (
                <p className="text-xs text-zinc-500 mt-1 animate-pulse">Gespeicherte Daten werden geladen...</p>
              )}
            </div>
            <div className="space-y-3">
              <button
                data-testid="variant-weekly"
                onClick={() => setPrefs(prev => ({ ...prev, activity_variant: 'weekly' }))}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  prefs.activity_variant === 'weekly' ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-7 h-7 text-amber-400 shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Wöchentlich</p>
                    <p className="text-zinc-400 text-sm">Festes Wochenziel inkl. Sport + Arbeit</p>
                    <p className="text-zinc-500 text-xs mt-1">Ideal für gleichbleibende Routinen</p>
                  </div>
                </div>
              </button>
              <button
                data-testid="variant-daily"
                onClick={() => setPrefs(prev => ({ ...prev, activity_variant: 'daily' }))}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  prefs.activity_variant === 'daily' ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Zap className="w-7 h-7 text-amber-400 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white">Täglich</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30 font-medium">BETA</span>
                    </div>
                    <p className="text-zinc-400 text-sm">BMR + tägliche Aktivität + Training + Nachbrenn</p>
                    <p className="text-zinc-500 text-xs mt-1">Passt sich jeden Tag an deine Aktivität an</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        );
      case 2:
        return <StepFitnessGoal prefs={prefs} setPrefs={setPrefs} calcSettings={calcSettings} dataLoaded={dataLoaded} />;
      case 3:
        return <StepBodyData prefs={prefs} setPrefs={setPrefs} bodyDataFromProfile={bodyDataFromProfile} />;
      case 4:
        return <StepSportsActivities prefs={prefs} setPrefs={setPrefs} />;
      case 5:
        return <StepDailyRoutine prefs={prefs} setPrefs={setPrefs} healthKitData={healthKitData} />;
      case 6:
        return <StepDietType prefs={prefs} setPrefs={setPrefs} />;
      case 7:
        return <StepCalorieGoal prefs={prefs} calculatedGoals={calculatedGoals} bodyComposition={bodyComposition} setPrefs={setPrefs} />;
      case 8:
        return (
          <StepAllergies
            prefs={prefs}
            setPrefs={setPrefs}
            newAvoidFood={newAvoidFood}
            setNewAvoidFood={setNewAvoidFood}
            addAvoidFood={addAvoidFood}
            removeAvoidFood={removeAvoidFood}
            toggleIntolerance={toggleIntolerance}
          />
        );
      case 9:
        return <StepFoodPreferences prefs={prefs} setPrefs={setPrefs} />;
      case 10:
        return <StepLifestyle prefs={prefs} setPrefs={setPrefs} />;
      case 11:
        return (
          <StepAIValidation
            prefs={prefs}
            calculatedGoals={calculatedGoals}
            token={token}
            isAdmin={isAdmin}
            customerId={customerId}
            onValidationComplete={(result) => setAiValidationResult(result)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Ernährungs-Einrichtung
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Schritt {currentEffectiveStep} von {effectiveSteps}
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <Progress value={progress} className="h-2 bg-zinc-700" />

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-yellow-500" />
            <p className="text-zinc-400 mt-2">Wird geladen...</p>
          </div>
        ) : (
          <>
            <div className="py-4">
              {renderStep()}
            </div>
            
            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t border-zinc-700">
              <div className="flex gap-2">
                <Button
                  data-testid="onboarding-back-btn"
                  variant="ghost"
                  onClick={handleBack}
                  disabled={step === 1}
                  className="text-zinc-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                
                {step === 1 && (
                  <Button
                    data-testid="onboarding-skip-btn"
                    variant="ghost"
                    onClick={handleSkip}
                    className="text-zinc-500 hover:text-zinc-300"
                  >
                    Später
                  </Button>
                )}
              </div>
              
              <Button
                data-testid="onboarding-next-btn"
                onClick={handleNext}
                disabled={!canProceed() || saving}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : step === totalSteps ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {step === totalSteps ? 'Fertig' : 'Weiter'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NutritionOnboarding;
